'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import type * as React from 'react';
import { useState } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Icon } from '@/components/ui/icon';
import { ArrowUpRight01Icon, Copy01Icon } from '@/components/ui/icon/glyphs';
import { SilkSheet } from '@/components/ui/silk-sheet';
import { TOAST_IDS } from '@/config/toast-ids';
import { onEvent } from '@/events';
import { showToast } from '@/lib/ui/toast';

/** Replaced with the participant's own rewards referral link at render time. */
const LINK_TOKEN = '{{link}}';
/** Sign-up page shown until the participant's referral link has loaded. */
const FALLBACK_LINK = 'https://morfi.markets/';

/**
 * Ready-to-post tweet ideas from the partner copy library (the text-only ones —
 * no video/content required). Each leads with the opinion-vs-prediction hook,
 * @mentions @morfimarkets (so copy-pasting qualifies for the post quest), and
 * ends with a sign-up CTA carrying the partner's own referral link via
 * {@link LINK_TOKEN}. Verbatim marketing copy → kept as data, not i18n.
 */
const POST_TEMPLATES: readonly {
  id: string;
  body: string;
}[] = [{
  id: 'new-category',
  body: 'how @morfimarkets is different from a prediction market:\n\nprediction markets need reality to resolve. morfi resolves on opinions + consensus — so any debate becomes a market, even ones polymarket could never touch.\n\na genuinely new category. try it: {{link}}'
}, {
  id: 'no-referee',
  body: "most apps make you guess what'll happen. @morfimarkets flips it: pick a side on a debate, back it with money, and the crowd decides the winner — no referee.\n\nopinion markets, not prediction markets. try it: {{link}}"
}, {
  id: 'on-chain-resolve',
  body: 'how do markets resolve on @morfimarkets? on-chain, by the protocol — zero humans in the loop.\n\nno admin, no oracle, no multisig picking winners. one side leads long enough and the protocol settles it automatically. proper engineering.\n\ntry it: {{link}}'
}, {
  id: 'any-debate',
  body: 'messi vs ronaldo. coke vs pepsi. best rapper alive.\n\non @morfimarkets every one of those is a real market — opinion markets, not prediction markets. the debates that never resolve in reality finally have somewhere to settle.\n\ntry it: {{link}}'
}, {
  id: 'manipulation-resistant',
  body: 'what sold me on @morfimarkets: the protocol was built from the ground up to resist manipulation, and every market resolves on-chain with zero humans in the loop.\n\nno oracle, no admin, no referee — just consensus.\n\ntry it: {{link}}'
}, {
  id: 'early-window',
  body: 'back what you actually believe: pick a side of a debate on @morfimarkets, put money on it, let the crowd decide the winner.\n\nopinion markets are a brand new category and this is the early window.\n\ntry it: {{link}}'
}];

/**
 * "Posts ↗" affordance shown beside the info glyph in the post-card header
 * (mirrors {@link import('./clips-button').ClipsButton}). Opens a modal of
 * ready-to-post tweet ideas — copy one, post it, earn points. Each CTA carries
 * the participant's own referral link so their post drives tracked sign-ups.
 */
export function PostsButton(t0) {
  const $ = _c(28);
  const {
    referralLink
  } = t0;
  const t = useTranslations("rewards");
  const [open, setOpen] = useState(false);
  const link = referralLink ?? FALLBACK_LINK;
  let t1;
  if ($[0] !== link) {
    t1 = body => body.split(LINK_TOKEN).join(link);
    $[0] = link;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const resolve = t1;
  let t2;
  if ($[2] !== t) {
    t2 = body_0 => {
      onEvent("rewards_content_kit_used", {
        kind: "post",
        action: "copy"
      });
      navigator.clipboard.writeText(body_0).then(() => {
        showToast({
          id: TOAST_IDS.REWARDS_POST_COPIED,
          message: t("post.postCopied"),
          variant: "success"
        });
      });
    };
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const copyPost = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      onEvent("rewards_content_kit_opened", {
        kind: "post"
      });
      setOpen(true);
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t) {
    t4 = t("post.postsLink");
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Icon icon={ArrowUpRight01Icon} className="size-3" />;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t4) {
    t6 = <button type="button" data-testid="open-posts" onClick={t3} className="inline-flex shrink-0 cursor-pointer items-center gap-0.5 text-caption font-medium text-foreground-secondary transition-colors hover:text-foreground-primary">{t4}{t5}</button>;
    $[8] = t4;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t) {
    t7 = t("post.postsTitle");
    $[10] = t;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t) {
    t8 = t("post.postsIntro");
    $[12] = t;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== copyPost || $[15] !== resolve || $[16] !== t) {
    t9 = POST_TEMPLATES.map(post => {
      const text = resolve(post.body);
      return <div key={post.id} className="flex flex-col gap-2.5 rounded-panel-row border p-3"><p className="whitespace-pre-line text-body text-foreground-secondary">{text}</p><AnimatedButton size="sm" variant="secondary" className="w-full" data-testid={`copy-post-${post.id}`} onClick={() => copyPost(text)}><Icon icon={Copy01Icon} className="size-3.5" />{t("post.copy")}</AnimatedButton></div>;
    });
    $[14] = copyPost;
    $[15] = resolve;
    $[16] = t;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t9) {
    t10 = <div className="flex flex-col gap-2">{t9}</div>;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== open || $[21] !== t10 || $[22] !== t7 || $[23] !== t8) {
    t11 = <SilkSheet open={open} onOpenChange={setOpen} variant="detached" title={t7} description={t8}>{t10}</SilkSheet>;
    $[20] = open;
    $[21] = t10;
    $[22] = t7;
    $[23] = t8;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t11 || $[26] !== t6) {
    t12 = <>{t6}{t11}</>;
    $[25] = t11;
    $[26] = t6;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  return t12;
}